import _ from 'lodash';
import React from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import CollapsibleButton from '../../components/CollapsibleButton';
import InfiniteScrollWaypoint from '../../components/InfiniteScrollWaypoint';
import { MenuItem } from '../../components/Menu';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import pinIcon from '../../icons/non-prefilled/16x16/pinOutline.svg';
import unpinIcon from '../../icons/non-prefilled/16x16/unpinOutline.svg';
import type { MetadataAttribute } from '../../post/PostRow';
import PostRow from '../../post/PostRow';
import PostSectionHeader from '../../post/PostSectionHeader';
import {
  PostPolicyAction,
  usePostPolicyCheck,
} from '../../post/postPolicyCheck';
import { Link } from '../../utils/router/v3Bridge';
import TopicPostOverflowMenuContent from './TopicPostOverflowMenuContent';
import styles from './TopicPostSection.module.scss';
import TopicPostsEmptyState from './TopicPostsEmptyState';
import TopicReorderablePosts from './TopicReorderablePosts';

const TOPIC_POST_SECTION_POST_FRAGMENT = gql(`
  fragment TopicPostSection_post on SlimPostFields {
    id
    ...PostRow_post
    ...TopicPostOverflowMenuContent_post
    ...usePostPolicyCheck_post
  }
`);

const TOPIC_POST_SECTION_FRAGMENT = gql(`
  fragment TopicPostSection_posts on TopicPostConnection {
    edges {
      ...TopicReorderablePosts_postEdges
      node {
        id
        ...TopicPostSection_post
      }
      pinnedOrder
      cursor
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
`);

interface TopicPostSectionProps {
  aboveTitle?: React.ReactNode;
  controls: React.ReactNode;
  fetchMore?: (() => void) | null;
  refetch: () => void;
  link?: string;
  metadataAttribute: MetadataAttribute | null;
  pinPost?: ((id: string, order: number) => Promise<unknown>) | null;
  unpinPost?: ((id: string) => void) | null;
  title: React.ReactNode;
  topicId: string;
  posts?: DocumentType<typeof TOPIC_POST_SECTION_FRAGMENT> | null;
  main?: boolean;
  loading: boolean;
}

const TopicPostSection = ({
  aboveTitle,
  controls,
  link,
  fetchMore,
  refetch,
  pinPost,
  unpinPost,
  metadataAttribute,
  posts,
  main,
  title,
  topicId,
  loading,
}: TopicPostSectionProps) => {
  const postPolicyCheck = usePostPolicyCheck();
  const currentUser = useAppSelector((state) => state.session.user);

  const edges = _.compact(posts?.edges ?? []);
  const postCommonProps = {
    seriesHoverCardEnabled: true,
    metadataAttribute,
  };
  const reorderableEdges = pinPost
    ? edges.filter((e) => e.pinnedOrder != null)
    : [];

  const maxPin = Math.max(
    -1,
    ...reorderableEdges.map(({ pinnedOrder }) => pinnedOrder ?? -1),
  );

  const staticEdges = _.compact(
    pinPost ? edges.filter((e) => e.pinnedOrder == null) : edges ?? [],
  );

  return (
    <div>
      {!!title && (
        <PostSectionHeader controls={controls} aboveTitle={aboveTitle}>
          {link ? (
            <h4>
              <Link className={styles.titleLink} to={link}>
                {title}
              </Link>
            </h4>
          ) : (
            <h3>{title}</h3>
          )}
        </PostSectionHeader>
      )}
      {!loading && posts && (
        <>
          {edges.length === 0 && main && (
            <TopicPostsEmptyState topicId={topicId} />
          )}
          {edges.length > 0 && (
            <div className={styles.posts}>
              {pinPost && reorderableEdges.length > 0 && (
                <TopicReorderablePosts
                  orderPost={pinPost}
                  postEdges={reorderableEdges}
                  renderPostEdge={({ node: post }) => {
                    if (post == null) return <>{null}</>;

                    const canEditPost = postPolicyCheck(
                      PostPolicyAction.EditPost,
                      post,
                      currentUser,
                    );

                    return (
                      <PostRow
                        {...postCommonProps}
                        renderOverflowMenu={(close) => (
                          <TopicPostOverflowMenuContent
                            post={post}
                            onClose={close}
                            canEditPost={canEditPost}
                            extraMenuItems={
                              <>
                                {unpinPost && (
                                  <MenuItem
                                    icon={unpinIcon}
                                    onClick={() => {
                                      unpinPost(post.id);
                                      close();
                                    }}
                                  >
                                    Unpin
                                  </MenuItem>
                                )}
                              </>
                            }
                            refetch={refetch}
                          />
                        )}
                        pinned
                        post={post}
                      />
                    );
                  }}
                />
              )}
              {staticEdges.map(({ node: post, pinnedOrder }) => {
                if (post == null) return null;

                const canEditPost = postPolicyCheck(
                  PostPolicyAction.EditPost,
                  post,
                  currentUser,
                );

                return (
                  <PostRow
                    {...postCommonProps}
                    key={post.id}
                    renderOverflowMenu={(close) => (
                      <TopicPostOverflowMenuContent
                        post={post}
                        onClose={close}
                        canEditPost={canEditPost}
                        extraMenuItems={
                          <>
                            {pinPost && (
                              <MenuItem
                                icon={pinIcon}
                                onClick={() => {
                                  pinPost(post.id, maxPin + 1);
                                  close();
                                }}
                              >
                                Pin
                              </MenuItem>
                            )}
                          </>
                        }
                        refetch={refetch}
                      />
                    )}
                    pinned={main ? pinnedOrder != null : undefined}
                    post={post}
                  />
                );
              })}
              {fetchMore &&
                (main ? (
                  <InfiniteScrollWaypoint fetchMore={fetchMore} />
                ) : (
                  <div className={styles.loadMoreContainer}>
                    <CollapsibleButton onClick={() => fetchMore()} collapsed>
                      Load more
                    </CollapsibleButton>
                  </div>
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

interface TopicPostSectionSimpleListProps {
  title: React.ReactNode;
  aboveTitle?: React.ReactNode;
  controls: React.ReactNode;
  metadataAttribute: MetadataAttribute | null;
  loading: boolean;
  posts: DocumentType<typeof TOPIC_POST_SECTION_POST_FRAGMENT>[];
  refetch: () => void;
}

export const TopicPostSectionSimpleList = ({
  title,
  aboveTitle,
  controls,
  posts,
  loading,
  refetch,
  metadataAttribute,
}: TopicPostSectionSimpleListProps) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const postPolicyCheck = usePostPolicyCheck();

  return (
    <div>
      {!!title && (
        <PostSectionHeader controls={controls} aboveTitle={aboveTitle}>
          <h3>{title}</h3>
        </PostSectionHeader>
      )}
      {!loading && posts.length > 0 && (
        <div className={styles.posts}>
          {posts.map((post) => {
            const canEditPost = postPolicyCheck(
              PostPolicyAction.EditPost,
              post,
              currentUser,
            );

            return (
              <PostRow
                seriesHoverCardEnabled
                metadataAttribute={metadataAttribute}
                key={post.id}
                renderOverflowMenu={(close) => (
                  <TopicPostOverflowMenuContent
                    post={post}
                    onClose={close}
                    canEditPost={canEditPost}
                    refetch={refetch}
                  />
                )}
                post={post}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TopicPostSection;
